/**
 * Locale-aware plural-form selection via `Intl.PluralRules`.
 *
 * Extracted from `editorStoreImportFileActions.ts` (the DXF-open status
 * message's `dxfio.noun.entity.*` / `dxfio.noun.space.*` selection) so the
 * Properties-panel aggregate hint (`inspector.aggregate_hint`,
 * `InspectorSidebar.tsx`) can reuse the SAME rule instead of re-deriving a
 * second hand-rolled mod10/mod100 Russian plural rule — Codex P2 on PR #58
 * already flagged a hand-rolled rule as locale-incorrect for English, and a
 * second independent implementation would risk repeating that mistake.
 *
 * Mapping: Russian CLDR returns one|few|many|other. English CLDR returns
 * one|other. The app's message keys use three slots — one|few|many — so
 * English "other" (and the unused "two"/"zero" categories some locales can
 * report) collapse to "many"; every English `*.few` key is written
 * identical to its `*.many` sibling, so this collapse is a no-op for the
 * text the user actually sees.
 */

import type { LanguageCode } from "./messageDomains";

export type PluralForm = "one" | "few" | "many";

/**
 * Resolve `n` items in `language` to the app's three-slot plural-form
 * vocabulary. Pure and side-effect free — safe to call from a React render
 * or a store action alike.
 */
export function pluralFormFor(n: number, language: LanguageCode): PluralForm {
  const pluralRules = new Intl.PluralRules(
    language === "ru" ? "ru-RU" : "en-US",
  );
  const category = pluralRules.select(n);
  if (category === "one") return "one";
  if (category === "few") return "few";
  return "many";
}
